import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeft, Save, Send, Loader2, FileText, Image as ImageIcon,
  Video as VideoIcon, File as FileIcon, Users, Tag as TagIcon, Layers,
} from "lucide-react";
import { LEAD_STATUSES } from "@/lib/constants";
import { groupTagsByCategory } from "@/lib/tagUtils";
import { useConfirm } from "@/hooks/useConfirm";

const TYPE_META = {
  text: { label: "Text", icon: FileText, tone: "text-slate-600" },
  image: { label: "Image", icon: ImageIcon, tone: "text-sky-600" },
  video: { label: "Video", icon: VideoIcon, tone: "text-violet-600" },
  document: { label: "Document", icon: FileIcon, tone: "text-amber-600" },
};

const STANDARD_FIELDS = [
  { key: "name", label: "Name" },
  { key: "phone", label: "Phone" },
  { key: "email", label: "Email" },
  { key: "city", label: "City" },
  { key: "country", label: "Country" },
];

export default function BroadcastEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;
  const confirm = useConfirm();

  const [name, setName] = useState("");
  const [audienceType, setAudienceType] = useState("all");
  const [tagIds, setTagIds] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [scheduledAt, setScheduledAt] = useState("");

  const [templates, setTemplates] = useState([]);
  const [tags, setTags] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [previewContactId, setPreviewContactId] = useState("");

  const [templateId, setTemplateId] = useState("");
  const [parameterMapping, setParameterMapping] = useState({});
  const [media, setMedia] = useState(null);  // {type, url, filename}
  const [audienceCount, setAudienceCount] = useState(null);
  const [renderedPreview, setRenderedPreview] = useState("");

  const [loading, setLoading] = useState(false);

  const template = useMemo(
    () => templates.find((t) => t.id === templateId) || null,
    [templates, templateId]
  );
  const templateType = template?.template_type || "text";
  const TypeIcon = TYPE_META[templateType]?.icon || FileText;

  // Initial loads
  useEffect(() => {
    api.get("/templates", { params: { status: "active" } }).then(({ data }) => setTemplates(data));
    api.get("/tags").then(({ data }) => setTags(data));
    api.get("/contacts", { params: { limit: 50 } }).then(({ data }) =>
      setContacts(data.items || data.contacts || data || [])
    );
    api.get("/custom-fields").then(({ data }) => setCustomFields(data)).catch(() => {});
    if (id) {
      api.get(`/broadcasts/${id}`).then(({ data }) => {
        const b = data.broadcast;
        setName(b.name);
        setAudienceType(b.audience?.type || "all");
        setTagIds(b.audience?.tag_ids || []);
        setStatuses(b.audience?.statuses || []);
        setScheduledAt(b.scheduled_at || "");
        setTemplateId(b.template_id || "");
        setParameterMapping(b.parameter_mapping || {});
        setMedia(b.media || null);
      });
    }
  }, [id]);

  // Audience preview
  useEffect(() => {
    const audience = { type: audienceType, tag_ids: tagIds, statuses, contact_ids: [] };
    api.post("/broadcasts/preview-audience", audience)
      .then(({ data }) => setAudienceCount(data.count))
      .catch(() => setAudienceCount(null));
  }, [audienceType, tagIds, statuses]);

  // Live preview render
  useEffect(() => {
    if (!template) { setRenderedPreview(""); return; }
    const ctrl = new AbortController();
    api.post("/broadcasts/preview-render", {
      body: template.body,
      parameter_mapping: parameterMapping,
      contact_id: previewContactId || null,
    }, { signal: ctrl.signal })
      .then(({ data }) => setRenderedPreview(data.rendered))
      .catch(() => {});
    return () => ctrl.abort();
  }, [template, parameterMapping, previewContactId]);

  // When template changes, reset mapping defaults to {position: ""}.
  useEffect(() => {
    if (!template) { setParameterMapping({}); setMedia(null); return; }
    setParameterMapping((prev) => {
      const next = {};
      (template.parameter_labels || []).forEach((p) => {
        next[String(p.position)] = prev[String(p.position)] || "";
      });
      return next;
    });
    // Reset media when template type changes if media incompatible.
    setMedia((prev) => {
      if (template.template_type === "text") return null;
      if (!prev || prev.type !== template.template_type) {
        return { type: template.template_type, url: "", filename: "" };
      }
      return prev;
    });
  }, [templateId, template]);

  const payload = () => ({
    name,
    template_id: templateId || null,
    message_body: template?.body || "",
    audience: { type: audienceType, tag_ids: tagIds, statuses, contact_ids: [] },
    scheduled_at: scheduledAt || null,
    status: scheduledAt ? "scheduled" : "draft",
    parameter_mapping: parameterMapping,
    media: (templateType !== "text" && media?.url) ? media : null,
  });

  const validate = () => {
    if (!name.trim()) { toast.error("Name required"); return false; }
    if (!template) { toast.error("Select a template"); return false; }
    // Every parameter_label must be mapped
    const missing = (template.parameter_labels || []).filter(
      (p) => !parameterMapping[String(p.position)]
    );
    if (missing.length) { toast.error(`Map "${missing[0].label}" first`); return false; }
    if (templateType !== "text" && (!media || !media.url)) {
      toast.error(`Provide a ${templateType} URL`);
      return false;
    }
    return true;
  };

  const saveDraft = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      if (isNew) {
        await api.post("/broadcasts", payload());
      } else {
        await api.patch(`/broadcasts/${id}`, payload());
      }
      toast.success("Saved");
      navigate("/broadcasts");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const sendNow = async () => {
    if (!validate()) return;
    const ok = await confirm({
      title: "Send broadcast now?",
      description: `${audienceCount ?? "?"} contacts will receive this message immediately.`,
      confirmText: "Send",
    });
    if (!ok) return;
    setLoading(true);
    try {
      let bcId = id;
      if (isNew) {
        const { data } = await api.post("/broadcasts", payload());
        bcId = data.id;
      } else {
        await api.patch(`/broadcasts/${id}`, payload());
      }
      const { data } = await api.post(`/broadcasts/${bcId}/send`);
      toast.success(`Sent to ${data?.stats?.sent ?? 0} contacts`);
      navigate(`/broadcasts/${bcId}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setLoading(false);
    }
  };

  const toggleTag = (tid) =>
    setTagIds(tagIds.includes(tid) ? tagIds.filter((t) => t !== tid) : [...tagIds, tid]);
  const toggleStatus = (sv) =>
    setStatuses(statuses.includes(sv) ? statuses.filter((s) => s !== sv) : [...statuses, sv]);

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto" data-testid="broadcast-editor-page">
      <Link to="/broadcasts" className="inline-flex items-center text-sm text-slate-500 hover:text-slate-900 mb-4">
        <ArrowLeft className="w-4 h-4 mr-1" />Back
      </Link>
      <PageHeader
        title={isNew ? "New broadcast" : "Edit broadcast"}
        description="3 steps: pick your audience, choose a template, then map fields and upload media."
        actions={
          <>
            <Button variant="outline" onClick={saveDraft} disabled={loading} data-testid="broadcast-save-draft">
              <Save className="w-4 h-4 mr-1.5" />Save draft
            </Button>
            <Button onClick={sendNow} disabled={loading} className="bg-brand hover:bg-brand-dark" data-testid="broadcast-send-now">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Send className="w-4 h-4 mr-1.5" />Send now</>)}
            </Button>
          </>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <Card className="border-slate-200 shadow-none">
            <CardContent className="p-6 space-y-4">
              <div>
                <Label className="text-xs font-semibold tracking-wider uppercase text-slate-600">Campaign name</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Festive Sale Announcement"
                  className="mt-1.5"
                  data-testid="broadcast-name-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* STEP 1: Audience */}
          <Card className="border-slate-200 shadow-none">
            <CardContent className="p-6">
              <SectionHeader step={1} icon={Users} title="Audience" />
              <Select value={audienceType} onValueChange={setAudienceType}>
                <SelectTrigger className="mt-2" data-testid="broadcast-audience-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All contacts</SelectItem>
                  <SelectItem value="tags">By tags</SelectItem>
                  <SelectItem value="status">By status</SelectItem>
                </SelectContent>
              </Select>

              {audienceType === "tags" && (
                <div className="mt-3 space-y-3">
                  {groupTagsByCategory(tags, { excludeInactive: true }).map(({ category, tags: catTags }) => (
                    <div key={category}>
                      <div className="text-[10px] font-semibold tracking-wider uppercase text-slate-400 mb-1.5">{category}</div>
                      <div className="flex flex-wrap gap-1.5">
                        {catTags.map((t) => (
                          <button
                            key={t.id}
                            onClick={() => toggleTag(t.id)}
                            className={`text-xs px-2 py-0.5 rounded-full border transition-colors ${
                              tagIds.includes(t.id) ? "text-white border-transparent" : "bg-white text-slate-600 border-slate-200"
                            }`}
                            style={tagIds.includes(t.id) ? { backgroundColor: t.color } : undefined}
                          >
                            {t.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {audienceType === "status" && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {LEAD_STATUSES.map((s) => (
                    <button
                      key={s.value}
                      onClick={() => toggleStatus(s.value)}
                      className={`text-xs px-2 py-0.5 rounded-full border transition-colors ${
                        statuses.includes(s.value) ? "bg-brand text-white border-brand" : "bg-white text-slate-600 border-slate-200"
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* STEP 2: Template */}
          <Card className="border-slate-200 shadow-none">
            <CardContent className="p-6">
              <SectionHeader step={2} icon={Layers} title="Template" />
              <Select value={templateId} onValueChange={setTemplateId}>
                <SelectTrigger className="mt-2" data-testid="broadcast-template-select">
                  <SelectValue placeholder="Select an active template" />
                </SelectTrigger>
                <SelectContent>
                  {templates.length === 0 && (
                    <div className="px-2 py-3 text-xs text-slate-400">
                      No active templates. <Link to="/templates" className="text-brand-dark underline">Create one →</Link>
                    </div>
                  )}
                  {templates.map((t) => {
                    const Icon = TYPE_META[t.template_type]?.icon || FileText;
                    return (
                      <SelectItem key={t.id} value={t.id} data-testid={`broadcast-template-option-${t.id}`}>
                        <span className="inline-flex items-center gap-2">
                          <Icon className="w-3.5 h-3.5 text-slate-500" />
                          {t.name} <span className="text-[10px] text-slate-400">· {t.category}</span>
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>

              {template && (
                <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3" data-testid="broadcast-template-preview">
                  <div className="flex items-center gap-2 text-xs text-slate-500 mb-1.5">
                    <TypeIcon className="w-3.5 h-3.5" />
                    <span className="font-semibold">{template.name}</span>
                    <Badge variant="outline" className="bg-white text-slate-600 border-slate-200 text-[10px]">{template.category}</Badge>
                  </div>
                  <pre className="text-xs text-slate-700 whitespace-pre-wrap font-sans">{template.body}</pre>
                </div>
              )}
            </CardContent>
          </Card>

          {/* STEP 3a: Media (conditional) */}
          {template && templateType !== "text" && (
            <Card className="border-slate-200 shadow-none">
              <CardContent className="p-6">
                <SectionHeader step={3} icon={TypeIcon} title={`${TYPE_META[templateType].label} upload`} />
                <div className="mt-2 space-y-2">
                  <Label className="text-xs font-semibold tracking-wider uppercase text-slate-600">{templateType} URL</Label>
                  <Input
                    value={media?.url || ""}
                    onChange={(e) => setMedia({ type: templateType, url: e.target.value, filename: media?.filename || "" })}
                    placeholder={`https://example.com/your-${templateType}.${templateType === "image" ? "jpg" : templateType === "video" ? "mp4" : "pdf"}`}
                    data-testid="broadcast-media-url"
                  />
                  {templateType === "document" && (
                    <div>
                      <Label className="text-xs font-semibold tracking-wider uppercase text-slate-600">Filename (optional)</Label>
                      <Input
                        value={media?.filename || ""}
                        onChange={(e) => setMedia({ ...(media || { type: templateType, url: "" }), filename: e.target.value })}
                        placeholder="invoice-jan-2026.pdf"
                        data-testid="broadcast-media-filename"
                      />
                    </div>
                  )}
                  <p className="text-[11px] text-slate-400">
                    Paste a publicly-reachable URL or a Meta-hosted media ID. (S3-style direct uploads are on the roadmap.)
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* STEP 3b: Parameter mapping */}
          {template && (template.parameter_labels || []).length > 0 && (
            <Card className="border-slate-200 shadow-none">
              <CardContent className="p-6">
                <SectionHeader step={template && templateType !== "text" ? 4 : 3} icon={TagIcon} title="Parameter mapping" />
                <p className="text-xs text-slate-500 mt-1">Map each label to a contact field. The values are substituted per contact at send time.</p>
                <div className="mt-3 space-y-2">
                  {template.parameter_labels.map((p) => (
                    <div key={p.position} className="grid grid-cols-[160px,1fr] gap-2 items-center" data-testid={`broadcast-param-${p.position}`}>
                      <div className="text-sm">
                        <div className="font-medium text-slate-800">{p.label}</div>
                        <code className="text-[10px] text-slate-400 font-mono">{`{{${p.position}}}`}</code>
                      </div>
                      <Select
                        value={parameterMapping[String(p.position)] || ""}
                        onValueChange={(v) =>
                          setParameterMapping((m) => ({ ...m, [String(p.position)]: v }))
                        }
                      >
                        <SelectTrigger data-testid={`broadcast-param-select-${p.position}`}><SelectValue placeholder="Choose a field" /></SelectTrigger>
                        <SelectContent>
                          <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Standard fields</div>
                          {STANDARD_FIELDS.map((f) => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}
                          {customFields.length > 0 && (
                            <>
                              <div className="px-2 py-1 mt-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Custom fields</div>
                              {customFields.map((f) => (
                                <SelectItem key={f.key} value={`custom_field.${f.key}`}>{f.label}</SelectItem>
                              ))}
                            </>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Schedule */}
          <Card className="border-slate-200 shadow-none">
            <CardContent className="p-6">
              <Label className="text-xs font-semibold tracking-wider uppercase text-slate-600">Schedule (optional)</Label>
              <Input
                type="datetime-local"
                value={scheduledAt ? scheduledAt.slice(0, 16) : ""}
                onChange={(e) => setScheduledAt(e.target.value ? new Date(e.target.value).toISOString() : "")}
                className="mt-1.5"
                data-testid="broadcast-schedule-input"
              />
              <p className="text-xs text-slate-500 mt-1">Leave empty to save as draft or send now.</p>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar: audience count + Preview Mode */}
        <div className="space-y-4">
          <Card className="border-slate-200 shadow-none">
            <CardContent className="p-6">
              <div className="text-xs font-semibold tracking-wider uppercase text-slate-600">Recipients</div>
              <div className="text-3xl font-semibold text-slate-900 mt-1" data-testid="broadcast-recipient-count">
                {audienceCount ?? "—"}
              </div>
              <p className="text-xs text-slate-500 mt-1">Contacts matching the chosen audience filter.</p>
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-none" data-testid="broadcast-preview-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs font-semibold tracking-wider uppercase text-slate-600">Preview</div>
                {templateType !== "text" && (
                  <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200 text-[10px]">
                    <TypeIcon className="w-3 h-3 mr-1" />{templateType}
                  </Badge>
                )}
              </div>
              <Select value={previewContactId || "__sample__"} onValueChange={(v) => setPreviewContactId(v === "__sample__" ? "" : v)}>
                <SelectTrigger className="mb-3" data-testid="broadcast-preview-contact"><SelectValue placeholder="Pick a contact to preview" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__sample__">Sample data</SelectItem>
                  {contacts.slice(0, 50).map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name || c.phone}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {media?.url && templateType === "image" && (
                <img src={media.url} alt="" className="w-full rounded-md border border-emerald-100 mb-2 object-cover max-h-44" data-testid="broadcast-preview-image" />
              )}
              {media?.url && templateType === "video" && (
                <div className="rounded-md border border-emerald-100 mb-2 bg-slate-900 text-white text-xs p-3 flex items-center gap-2">
                  <VideoIcon className="w-4 h-4" /> Video: {media.url.split("/").pop()}
                </div>
              )}
              {media?.url && templateType === "document" && (
                <div className="rounded-md border border-emerald-100 mb-2 bg-amber-50 text-amber-900 text-xs p-3 flex items-center gap-2" data-testid="broadcast-preview-doc">
                  <FileIcon className="w-4 h-4" /> {media.filename || media.url.split("/").pop()}
                </div>
              )}

              <div
                className="rounded-md bg-emerald-50 border border-emerald-100 p-3 text-sm text-slate-800 whitespace-pre-wrap"
                data-testid="broadcast-preview-body"
              >
                {renderedPreview || "Pick a template to see the preview."}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function SectionHeader({ step, icon: Icon, title }) {
  return (
    <div className="flex items-center gap-2">
      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-brand text-white text-[10px] font-semibold">{step}</span>
      <Icon className="w-4 h-4 text-slate-500" />
      <span className="text-sm font-semibold text-slate-800">{title}</span>
    </div>
  );
}
